/**
 * The URL fields of a RUM event that `beforeSend` may rewrite. Structural, so
 * any RUM event satisfies it.
 */
type EventWithUrls = {
  type: string;
  view: {
    url: string;
    referrer?: string;
    performance?: { lcp?: { resource_url?: string } };
  };
  resource?: { url: string };
  error?: {
    message?: string;
    stack?: string;
    handling_stack?: string;
    resource?: { url: string };
  };
  long_task?: { scripts?: { source_url?: string }[] };
};

/** A URL up to its query string or fragment, whichever comes first. */
const withoutQuery = (url: string): string => {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
};

/** Every URL written in a piece of text, up to its query string or fragment. */
const URL_IN_TEXT = /(\b[a-z][a-z0-9+.-]*:\/\/[^\s?#"'`)]+)[?#][^\s"'`)]*/gi;

const withoutQueriesIn = (text: string): string =>
  text.replace(URL_IN_TEXT, "$1");

const isOwn = (url: string, ownOrigins: readonly string[]): boolean => {
  try {
    return ownOrigins.includes(new URL(url).origin);
  } catch {
    return false;
  }
};

/**
 * Makes a RUM event fit to leave the browser, rewriting it in place; `false`
 * drops it.
 *
 * Every query string and fragment goes, not only the ones known to carry
 * something: what a page puts there can be something typed about a child, and a
 * parameter added later would follow it into Datadog without anybody deciding
 * it should.
 *
 * A resource from any origin but Haven's own is dropped whole, and so is an
 * error about one. What a third party puts in its URLs is not ours to vouch
 * for, and an address lookup sends the address being typed. A URL written into
 * an error's message or stack — a blocked request a content security report
 * names — keeps its path and loses the rest.
 */
export const keepRumEventPrivate = (
  event: EventWithUrls,
  ownOrigins: readonly string[],
): boolean => {
  if (event.type === "resource" && event.resource !== undefined) {
    if (!isOwn(event.resource.url, ownOrigins)) {
      return false;
    }

    event.resource.url = withoutQuery(event.resource.url);
  }

  event.view.url = withoutQuery(event.view.url);

  if (event.view.referrer !== undefined) {
    event.view.referrer = withoutQuery(event.view.referrer);
  }

  const lcp = event.view.performance?.lcp;
  if (lcp?.resource_url !== undefined) {
    lcp.resource_url = withoutQuery(lcp.resource_url);
  }

  if (event.error !== undefined) {
    const { error } = event;

    if (error.resource !== undefined) {
      if (!isOwn(error.resource.url, ownOrigins)) {
        return false;
      }
      error.resource.url = withoutQuery(error.resource.url);
    }

    if (error.message !== undefined) {
      error.message = withoutQueriesIn(error.message);
    }
    if (error.stack !== undefined) {
      error.stack = withoutQueriesIn(error.stack);
    }
    if (error.handling_stack !== undefined) {
      error.handling_stack = withoutQueriesIn(error.handling_stack);
    }
  }

  for (const script of event.long_task?.scripts ?? []) {
    if (script.source_url !== undefined) {
      script.source_url = withoutQuery(script.source_url);
    }
  }

  return true;
};
